import { ArrowDownward, ArrowUpward, Close, Delete } from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { createAlbum, deleteAlbum, updateAlbum } from "api";
import { useSelectedOrganization, useSnackbar, useTranslations } from "hooks";
import { getTranslation } from "common";
import { Album, AlbumImage } from "model/albumModel";
import ConfirmDialog from "./ConfirmDialog";

export interface AlbumCandidate {
  id: number;
  filename: string;
  thumb?: string;
}

interface AlbumEditDialogProps {
  open: boolean;
  onClose: () => void;
  idprocess: number;
  /** null = crea; album esistente = modifica. */
  album: Album | null;
  candidates: AlbumCandidate[];
  onSaved: () => void;
}

/**
 * Crea o modifica un album: nome, selezione delle immagini tra gli allegati,
 * riordino con frecce su/giù e didascalia per foto.
 */
const AlbumEditDialog: React.FC<AlbumEditDialogProps> = ({
  open,
  onClose,
  idprocess,
  album,
  candidates,
  onSaved,
}) => {
  const translations = useTranslations();
  const { sendSnack } = useSnackbar();
  const organization = useSelectedOrganization();

  const [name, setName] = useState("");
  const [items, setItems] = useState<AlbumImage[]>([]);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(album?.name ?? "");
    setItems(album ? album.images.map((im) => ({ ...im })) : []);
  }, [open, album]);

  const byId = useMemo(() => new Map(candidates.map((c) => [c.id, c])), [candidates]);
  const selectedIds = useMemo(() => new Set(items.map((im) => im.idupload)), [items]);

  const toggle = (id: number) => {
    setItems((prev) =>
      prev.some((im) => im.idupload === id)
        ? prev.filter((im) => im.idupload !== id)
        : [...prev, { idupload: id, caption: "" }]
    );
  };

  const move = (index: number, delta: number) => {
    setItems((prev) => {
      const next = [...prev];
      const target = index + delta;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const setCaption = (id: number, caption: string) =>
    setItems((prev) => prev.map((im) => (im.idupload === id ? { ...im, caption } : im)));

  const canSave = name.trim().length > 0 && items.length > 0 && !busy;

  const handleSave = async () => {
    if (!organization?.id) return;
    setBusy(true);
    try {
      if (album) {
        await updateAlbum(album.id, organization.id, name.trim(), items);
      } else {
        await createAlbum(idprocess, organization.id, name.trim(), items);
      }
      sendSnack({
        type: "success",
        message: album
          ? getTranslation(translations, "album.updated")
          : getTranslation(translations, "album.created"),
      });
      onSaved();
      onClose();
    } catch (e: any) {
      sendSnack({
        type: "error",
        message: e?.response?.data?.detail || getTranslation(translations, "album.save_failed"),
      });
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!organization?.id || !album) return;
    setBusy(true);
    try {
      await deleteAlbum(album.id, organization.id);
      sendSnack({
        type: "success",
        message: getTranslation(translations, "album.deleted"),
      });
      onSaved();
      onClose();
    } catch (e: any) {
      sendSnack({
        type: "error",
        message: e?.response?.data?.detail || getTranslation(translations, "album.delete_failed"),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {album
            ? getTranslation(translations, "album.edit.title")
            : getTranslation(translations, "album.new.title")}
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            id="album-name"
            label={getTranslation(translations, "album.name")}
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            size="small"
            sx={{ mb: 2 }}
          />

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {getTranslation(translations, "album.selected")} ({items.length})
          </Typography>
          {items.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {getTranslation(translations, "album.selected.empty")}
            </Typography>
          ) : (
            <Stack spacing={1} sx={{ mb: 2 }}>
              {items.map((im, index) => (
                <Stack
                  key={im.idupload}
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 0.5 }}
                >
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      flex: "0 0 auto",
                      borderRadius: 1,
                      overflow: "hidden",
                      bgcolor: "action.hover",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {byId.get(im.idupload)?.thumb ? (
                      <Box
                        component="img"
                        src={byId.get(im.idupload)!.thumb}
                        alt=""
                        sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                      />
                    ) : null}
                  </Box>
                  <TextField
                    value={im.caption}
                    onChange={(e) => setCaption(im.idupload, e.target.value)}
                    placeholder={byId.get(im.idupload)?.filename ?? ""}
                    size="small"
                    fullWidth
                  />
                  <IconButton
                    onClick={() => move(index, -1)}
                    disabled={index === 0}
                    size="small"
                    aria-label="su"
                  >
                    <ArrowUpward fontSize="small" />
                  </IconButton>
                  <IconButton
                    onClick={() => move(index, 1)}
                    disabled={index === items.length - 1}
                    size="small"
                    aria-label="giù"
                  >
                    <ArrowDownward fontSize="small" />
                  </IconButton>
                  <IconButton onClick={() => toggle(im.idupload)} size="small" aria-label="rimuovi">
                    <Close fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
            </Stack>
          )}

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {getTranslation(translations, "album.candidates")}
          </Typography>
          {candidates.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {getTranslation(translations, "album.candidates.empty")}
            </Typography>
          ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(80px, 1fr))",
                gap: 1,
              }}
            >
              {candidates.map((c) => {
                const selected = selectedIds.has(c.id);
                return (
                  <Box
                    key={c.id}
                    onClick={() => toggle(c.id)}
                    sx={{
                      position: "relative",
                      aspectRatio: "1 / 1",
                      borderRadius: 1,
                      overflow: "hidden",
                      border: "2px solid",
                      borderColor: selected ? "primary.main" : "divider",
                      bgcolor: "action.hover",
                      cursor: "pointer",
                    }}
                  >
                    {c.thumb ? (
                      <Box
                        component="img"
                        src={c.thumb}
                        alt={c.filename}
                        sx={{
                          position: "absolute",
                          inset: 0,
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          display: "block",
                        }}
                      />
                    ) : null}
                    {/* Badge sempre sopra l'immagine (zIndex), non interattivo:
                        il click lo gestisce la tile intera. Così il check non
                        "sparisce" passando il cursore sull'immagine. */}
                    <Box
                      sx={{
                        position: "absolute",
                        top: 2,
                        left: 2,
                        zIndex: 2,
                        borderRadius: 1,
                        bgcolor: "background.paper",
                        boxShadow: 1,
                        display: "flex",
                        pointerEvents: "none",
                      }}
                    >
                      <Checkbox
                        checked={selected}
                        size="small"
                        tabIndex={-1}
                        disableRipple
                        sx={{ p: 0.25, pointerEvents: "none" }}
                      />
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          {album && (
            <Button
              color="error"
              onClick={() => setConfirmDelete(true)}
              disabled={busy}
              startIcon={<Delete />}
            >
              {getTranslation(translations, "settings.delete.label")}
            </Button>
          )}
          <Box sx={{ flex: 1 }} />
          <Button onClick={onClose} color="inherit" disabled={busy}>
            {getTranslation(translations, "generic.no.caption")}
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={!canSave} id="album-save">
            {getTranslation(translations, "pm.button.save")}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title={getTranslation(translations, "settings.delete.label")}
        content={getTranslation(translations, "album.delete.confirm")}
        cancelText={getTranslation(translations, "generic.no.caption")}
        confirmText={getTranslation(translations, "generic.yes.caption")}
        onConfirm={() => {
          setConfirmDelete(false);
          handleDelete();
        }}
      />
    </>
  );
};

export default AlbumEditDialog;
